package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.CreateNoticeRequest
import com.elan.edubridge.shared.domain.model.NoticeData
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.UpdateNoticeRequest
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.noticeBodyHtml
import com.elan.edubridge.ui.shell.plainTextToHtml
import com.elan.edubridge.ui.staff.externals.Quill
import com.elan.edubridge.ui.shell.formatTimestamp
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

/** 학원 전체 공지 피드. 작성·수정·삭제는 EDITOR 이상만 가능하다. */
object NoticesPage {
    private val scope = MainScope()

    /** 공지 조회 실패의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "staff-notices-retry"
    private val repository = SharedGraph.createRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun open(session: SessionState, academyId: String) {
        scope.launch {
            val result = repository.noticesResult(academyId)
            renderList(
                session,
                academyId,
                result.items.sortedByDescending { it.date },
                result.failure,
            )
        }
    }

    private fun renderList(
        session: SessionState,
        academyId: String,
        notices: List<NoticeData>,
        failure: CommandFailure?,
    ) {
        DashboardShell.render(session, academyId, "notices", contentHtml(session, notices, failure)) {
            wireRetry(RETRY_ID) { open(session, academyId) }
            wireList(session, academyId, notices)
        }
    }

    private fun contentHtml(
        session: SessionState,
        notices: List<NoticeData>,
        failure: CommandFailure?,
    ): String {
        val canManage = session.activePermission.atLeast(Permission.EDITOR)
        val createButton = if (canManage) {
            """<button class="eb-button eb-button--primary" id="create-notice" type="button">${icon("icon-plus", "sm")}공지 작성</button>"""
        } else {
            ""
        }
        // 못 읽은 것을 "등록된 공지가 없어요"로 그리면 스태프가 자기 공지가 사라진 줄 안다(#30).
        val list = when {
            notices.isNotEmpty() -> notices.joinToString("") { notice -> noticeListItemHtml(notice, canManage) }
            failure != null -> readErrorHtml(failure, RETRY_ID)
            else ->
                """<div class="eb-empty-state">${icon("icon-notice", "xl")}<h2>등록된 공지가 없어요</h2><p>학원 전체에 전달할 공지가 여기에 표시돼요.</p></div>"""
        }

        return """
            <div class="eb-page-header">
                <h1 class="eb-page-title">공지</h1>
                $createButton
            </div>
            <section class="eb-card" aria-label="학원 공지 목록">$list</section>
        """.trimIndent()
    }

    private fun noticeListItemHtml(notice: NoticeData, canManage: Boolean): String {
        val author = notice.authorName?.takeIf { it.isNotBlank() }?.let { " · ${it.escapeHtml()}" }.orEmpty()
        val actions = if (canManage) {
            """
            <div class="eb-notice-actions">
                <button class="eb-button eb-button--ghost eb-button--sm edit-notice" type="button" data-notice-id="${notice.id.escapeHtml()}">수정</button>
                <button class="eb-button eb-button--ghost eb-button--sm delete-notice" type="button" data-notice-id="${notice.id.escapeHtml()}">삭제</button>
            </div>
            """.trimIndent()
        } else {
            ""
        }
        return """
            <article class="eb-notice-item">
                ${icon("icon-notice", "md")}
                <div>
                    <button class="eb-button eb-button--link notice-open" type="button" data-notice-id="${notice.id.escapeHtml()}">${notice.title.escapeHtml()}</button>
                    <span class="eb-meta">${formatTimestamp(notice.date)}$author</span>
                    <p>${preview(notice.content).escapeHtml()}</p>
                </div>
                $actions
            </article>
        """.trimIndent()
    }

    private fun wireList(session: SessionState, academyId: String, notices: List<NoticeData>) {
        (document.getElementById("create-notice") as? HTMLButtonElement)?.onclick = {
            openEditor(session, academyId, existing = null)
            null
        }
        val openButtons = document.querySelectorAll(".notice-open")
        for (index in 0 until openButtons.length) {
            val button = openButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                notices.firstOrNull { it.id == button.getAttribute("data-notice-id") }?.let {
                    renderDetail(session, academyId, it, notices)
                }
                null
            }
        }
        wireNoticeButtons(session, academyId, notices)
    }

    private fun renderDetail(session: SessionState, academyId: String, notice: NoticeData, notices: List<NoticeData>) {
        val canManage = session.activePermission.atLeast(Permission.EDITOR)
        val author = notice.authorName?.takeIf { it.isNotBlank() }?.let { " · ${it.escapeHtml()}" }.orEmpty()
        DashboardShell.render(session, academyId, "notices", """
            <div class="eb-page-header">
                <button class="eb-button eb-button--ghost" id="notice-back" type="button">목록으로</button>
                ${if (canManage) """
                    <div>
                        <button class="eb-button eb-button--ghost" id="notice-edit" type="button">수정</button>
                        <button class="eb-button eb-button--ghost" id="notice-delete" type="button">삭제</button>
                    </div>
                """ else ""}
            </div>
            <article class="eb-card" aria-labelledby="notice-title">
                <h1 class="eb-page-title" id="notice-title">${notice.title.escapeHtml()}</h1>
                <p class="eb-meta">${formatTimestamp(notice.date)}$author</p>
                ${noticeBodyHtml(notice.contentHtml, notice.content)}
            </article>
        """.trimIndent()) {
            (document.getElementById("notice-back") as? HTMLButtonElement)?.onclick = {
                // 상세에서 돌아올 때 쓰는 목록은 이미 성공적으로 읽어 둔 것이다.
                renderList(session, academyId, notices, failure = null)
                null
            }
            (document.getElementById("notice-edit") as? HTMLButtonElement)?.onclick = {
                openEditor(session, academyId, notice)
                null
            }
            (document.getElementById("notice-delete") as? HTMLButtonElement)?.onclick = {
                openDeleteConfirmation(session, academyId, notice)
                null
            }
        }
    }

    private fun wireNoticeButtons(session: SessionState, academyId: String, notices: List<NoticeData>) {
        val editButtons = document.querySelectorAll(".edit-notice")
        for (index in 0 until editButtons.length) {
            val button = editButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                val noticeId = button.getAttribute("data-notice-id")
                notices.firstOrNull { it.id == noticeId }?.let { openEditor(session, academyId, it) }
                null
            }
        }

        val deleteButtons = document.querySelectorAll(".delete-notice")
        for (index in 0 until deleteButtons.length) {
            val button = deleteButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                val noticeId = button.getAttribute("data-notice-id")
                notices.firstOrNull { it.id == noticeId }?.let { openDeleteConfirmation(session, academyId, it) }
                null
            }
        }
    }

    private fun openEditor(session: SessionState, academyId: String, existing: NoticeData?) {
        val isCreating = existing == null
        val backdrop = mountModal(
            labelledBy = "notice-editor-title",
            """
            <header class="eb-modal__header">
                <h2 id="notice-editor-title">${if (isCreating) "공지 작성" else "공지 수정"}</h2>
                <button class="eb-icon-button" id="notice-editor-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
            </header>
            <form class="eb-modal__body" id="notice-editor-form">
                <div class="eb-field">
                    <label for="notice-title">제목</label>
                    <input id="notice-title" maxlength="100" value="${existing?.title?.escapeHtml().orEmpty()}" required>
                </div>
                <div class="eb-field">
                    <label id="notice-content-label">내용</label>
                    <div class="eb-editor"><div id="notice-editor" aria-labelledby="notice-content-label"></div></div>
                    <p class="eb-meta">학원 전체 구성원에게 전달돼요. 굵게·제목·목록·링크를 쓸 수 있어요.</p>
                </div>
                <p class="eb-form-error" role="alert" id="notice-editor-error" hidden></p>
            </form>
            <footer class="eb-modal__footer">
                <button class="eb-button eb-button--ghost" id="notice-editor-cancel" type="button">취소</button>
                <button class="eb-button eb-button--primary" id="notice-editor-submit" type="submit" form="notice-editor-form">${if (isCreating) "발행" else "저장"}</button>
            </footer>
            """.trimIndent(),
        )

        var isSubmitting = false
        fun close() = backdrop.remove()
        fun setSubmitting(pending: Boolean) {
            isSubmitting = pending
            listOf("#notice-editor-close", "#notice-editor-cancel", "#notice-editor-submit").forEach { selector ->
                (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = pending
            }
        }
        (backdrop.querySelector("#notice-editor-close") as? HTMLElement)?.onclick = {
            if (!isSubmitting) close()
            null
        }
        (backdrop.querySelector("#notice-editor-cancel") as? HTMLElement)?.onclick = {
            if (!isSubmitting) close()
            null
        }

        val editorHost = backdrop.querySelector("#notice-editor") as? HTMLElement ?: return
        val quill = mountQuill(editorHost)
        // 수정이면 저장된 본문으로 시작한다. 서식 공지는 서버 살균본 그대로, 평문 공지는 문단으로 감싸서.
        existing?.let { quill.clipboard.dangerouslyPasteHTML(it.contentHtml ?: plainTextToHtml(it.content), "silent") }
        // "변경 없음" 판정의 기준. 붙여 넣은 직후의 시맨틱 HTML 이 곧 Quill 이 이 본문을 표현하는 형태다.
        val initialHtml = quill.getSemanticHTML()

        val form = backdrop.querySelector("#notice-editor-form") as? HTMLFormElement ?: return
        form.addEventListener("submit", { event ->
            event.preventDefault()
            if (!isSubmitting) {
                submitEditor(session, academyId, existing, backdrop, quill, initialHtml, ::close, ::setSubmitting)
            }
        })
        (backdrop.querySelector("#notice-title") as? HTMLInputElement)?.focus()
    }

    /**
     * 툴바는 서버 허용 태그와 일대일이다(제목 2단계·굵게·기울임·밑줄·취소선·목록 2종·인용·링크). 색·크기·표는
     * 넣지 않는다 — 넣어도 서버가 지운다. `js()` 객체는 `.also {}` 없이 명시 대입만 쓴다(QrRenderer.kt 함정).
     */
    private fun mountQuill(host: HTMLElement): Quill {
        val options: dynamic = js("({})")
        options.theme = "snow"
        options.placeholder = "내용을 입력해 주세요."
        val modules: dynamic = js("({})")
        modules.toolbar = js(
            "[[{ header: [2, 3, false] }], ['bold', 'italic', 'underline', 'strike'], " +
                "[{ list: 'ordered' }, { list: 'bullet' }], ['blockquote', 'link'], ['clean']]",
        )
        options.modules = modules
        return Quill(host, options)
    }

    private fun submitEditor(
        session: SessionState,
        academyId: String,
        existing: NoticeData?,
        backdrop: HTMLElement,
        quill: Quill,
        initialHtml: String,
        close: () -> Unit,
        setSubmitting: (Boolean) -> Unit,
    ) {
        val title = (backdrop.querySelector("#notice-title") as? HTMLInputElement)?.value?.trim().orEmpty()
        // 글자 수는 태그를 뺀 본문 기준 — 서버(`htmlToPlainText`)도 같은 기준으로 5,000자를 센다.
        val html = quill.getSemanticHTML()
        val plain = quill.getText().trim()
        val error = backdrop.querySelector("#notice-editor-error") as? HTMLElement ?: return
        val submit = backdrop.querySelector("#notice-editor-submit") as? HTMLButtonElement ?: return
        if (submit.disabled) return

        if (title.isEmpty() || plain.isEmpty()) {
            showError(error, "제목과 내용을 모두 입력해 주세요.")
            return
        }
        if (title.length > 100 || plain.length > 5000) {
            showError(error, "제목은 100자, 내용은 5,000자까지 입력할 수 있어요.")
            return
        }
        if (existing != null && title == existing.title && html == initialHtml) {
            showError(error, "변경된 내용이 없어요.")
            return
        }

        setSubmitting(true)
        scope.launch {
            try {
                val outcome = if (existing == null) {
                    // `content` 는 서버가 HTML 에서 다시 뽑는다 — 여기 보내는 평문은 계약상의 자리 채움이다.
                    commandRepository.createNotice(CreateNoticeRequest(academyId, title, content = plain, contentHtml = html)).let {
                        it.isSuccess to it.errorMessage
                    }
                } else {
                    commandRepository.updateNotice(
                        UpdateNoticeRequest(
                            academyId = academyId,
                            noticeId = existing.id,
                            title = title.takeIf { it != existing.title },
                            contentHtml = html.takeIf { it != initialHtml },
                        ),
                    ).let { it.isSuccess to it.errorMessage }
                }
                if (outcome.first) {
                    close()
                    Toast.success(if (existing == null) "공지를 발행했어요." else "공지를 수정했어요.")
                    open(session, academyId)
                } else {
                    showError(error, outcome.second ?: "저장에 실패했어요. 다시 시도해 주세요.")
                }
            } finally {
                setSubmitting(false)
            }
        }
    }

    private fun openDeleteConfirmation(session: SessionState, academyId: String, notice: NoticeData) {
        val backdrop = mountModal(
            labelledBy = "notice-delete-title",
            """
            <header class="eb-modal__header">
                <h2 id="notice-delete-title">공지를 삭제할까요?</h2>
                <button class="eb-icon-button" id="notice-delete-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
            </header>
            <div class="eb-modal__body">
                <p><strong>${notice.title.escapeHtml()}</strong></p>
                <p>삭제한 공지는 구성원에게 더 이상 보이지 않아요.</p>
                <p class="eb-form-error" role="alert" id="notice-delete-error" hidden></p>
            </div>
            <footer class="eb-modal__footer">
                <button class="eb-button eb-button--ghost" id="notice-delete-cancel" type="button">취소</button>
                <button class="eb-button eb-button--primary" id="notice-delete-confirm" type="button">삭제</button>
            </footer>
            """.trimIndent(),
        )

        var isSubmitting = false
        fun close() = backdrop.remove()
        fun setSubmitting(pending: Boolean) {
            isSubmitting = pending
            listOf("#notice-delete-close", "#notice-delete-cancel", "#notice-delete-confirm").forEach { selector ->
                (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = pending
            }
        }
        (backdrop.querySelector("#notice-delete-close") as? HTMLElement)?.onclick = {
            if (!isSubmitting) close()
            null
        }
        (backdrop.querySelector("#notice-delete-cancel") as? HTMLElement)?.onclick = {
            if (!isSubmitting) close()
            null
        }

        val confirm = backdrop.querySelector("#notice-delete-confirm") as? HTMLButtonElement ?: return
        confirm.onclick = {
            if (!isSubmitting) {
                setSubmitting(true)
                scope.launch {
                    try {
                        val result = commandRepository.deleteNotice(academyId, notice.id)
                        if (result.isSuccess) {
                            close()
                            Toast.success("공지를 삭제했어요.")
                            open(session, academyId)
                        } else {
                            showError(
                                backdrop.querySelector("#notice-delete-error") as? HTMLElement,
                                result.errorMessage ?: "삭제에 실패했어요. 다시 시도해 주세요.",
                            )
                        }
                    } finally {
                        setSubmitting(false)
                    }
                }
            }
            null
        }
        confirm.focus()
    }

    private fun mountModal(labelledBy: String, contentHtml: String): HTMLElement {
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <section class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="$labelledBy">
                $contentHtml
            </section>
        """.trimIndent()
        document.body?.appendChild(backdrop)
        return backdrop
    }

    private fun showError(element: HTMLElement?, message: String) {
        element ?: return
        element.textContent = message
        element.removeAttribute("hidden")
    }

    private fun preview(content: String): String {
        val compact = content.replace(Regex("\\s+"), " ").trim()
        return if (compact.length > 120) "${compact.take(120)}…" else compact
    }

}
